


import {
  computed,
  defineComponent,
  PropType,
  ref,
} from '@vue/composition-api';
import type {
  HokkaidoAirportInspectionCategory,
  HokkaidoAirportInspectionMaster,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import type {
  HokkaidoAirportInspectionResults,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportInspection';
import {
  filterInspectionCategories,
  getInspectionCarTypes,
  isInspectionCompleted,
} from '@/lib/externalReports/hokkaidoAirportInspection';

export default defineComponent({
  props: {
    master: { type: Object as PropType<HokkaidoAirportInspectionMaster>, required: true },
    airportName: { type: String, required: true },
    carType: { type: String, required: true },
    titleLabel: { type: String, required: true },
    commentLabel: { type: String, required: true },
    value: { type: Object as PropType<HokkaidoAirportInspectionResults>, required: true },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const inspectionCarTypes = computed<string[]>(() => {
      return getInspectionCarTypes(props.master, props.airportName, props.carType);
    });

    const categoriesByCarType = computed<Record<string, HokkaidoAirportInspectionCategory[]>>(() => {
      return Object.fromEntries(inspectionCarTypes.value.map(carType => {
        return [carType, filterInspectionCategories(props.master, props.airportName, carType)];
      }));
    });

    const openedCategoryKeys = ref<string[]>([]);

    const getSelectedSymbol = (carType: string, itemKey: string): string => {
      return props.value[carType]?.items[itemKey] ?? '';
    };

    const isCategoryOpened = (carType: string, categoryKey: string): boolean => {
      return openedCategoryKeys.value.includes(`${carType}:${categoryKey}`);
    };

    const toggleCategory = (carType: string, categoryKey: string): void => {
      const openKey = `${carType}:${categoryKey}`;
      openedCategoryKeys.value = isCategoryOpened(carType, categoryKey)
        ? openedCategoryKeys.value.filter(key => key !== openKey)
        : [...openedCategoryKeys.value, openKey];
    };

    const isCategoryCompleted = (carType: string, category: HokkaidoAirportInspectionCategory): boolean => {
      return isInspectionCompleted([category], props.value[carType]);
    };

    const updateItem = (carType: string, itemKey: string, symbolKey: string): void => {
      const current = props.value[carType] ?? { items: {}, comment: '' };
      emit('update', {
        ...props.value,
        [carType]: { ...current, items: { ...current.items, [itemKey]: symbolKey } },
      });
    };

    const updateComment = (carType: string, comment: string): void => {
      const current = props.value[carType] ?? { items: {}, comment: '' };
      emit('update', { ...props.value, [carType]: { ...current, comment } });
    };

    return {
      inspectionCarTypes,
      categoriesByCarType,
      getSelectedSymbol,
      isCategoryOpened,
      toggleCategory,
      isCategoryCompleted,
      updateItem,
      updateComment,
    };
  },
});
